// src/styles/ThemeScope.tsx
import { PropsWithChildren, useMemo } from 'react';

export type ThemeInput = Partial<{
  primary90: string; primary50: string; primary20: string; primary10: string;
  secondary90: string; secondary50: string; secondary20: string; secondary10: string;
  neutral90: string; neutral50: string; neutral20: string; neutral10: string;
  accentRed90: string; accentRed50: string; accentRed10: string;
}>;

function hexToRgbSpace(hex?: string): string | undefined {
  if (!hex) return;
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const bigint = parseInt(full, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r} ${g} ${b}`;
}

export default function ThemeScope({
  theme,
  children,
}: PropsWithChildren<{ theme?: ThemeInput }>) {
  const style = useMemo<React.CSSProperties>(() => {
    if (!theme) return {};
    const s: Record<string, string> = {};

    if (theme.primary90) s['--pop-primary-90'] = hexToRgbSpace(theme.primary90)!;
    if (theme.primary50) s['--pop-primary-50'] = hexToRgbSpace(theme.primary50)!;
    if (theme.primary20) s['--pop-primary-20'] = hexToRgbSpace(theme.primary20)!;
    if (theme.primary10) s['--pop-primary-10'] = hexToRgbSpace(theme.primary10)!;

    if (theme.secondary90) s['--pop-secondary-90'] = hexToRgbSpace(theme.secondary90)!;
    if (theme.secondary50) s['--pop-secondary-50'] = hexToRgbSpace(theme.secondary50)!;
    if (theme.secondary20) s['--pop-secondary-20'] = hexToRgbSpace(theme.secondary20)!;
    if (theme.secondary10) s['--pop-secondary-10'] = hexToRgbSpace(theme.secondary10)!;

    if (theme.neutral90) s['--pop-neutral-90'] = hexToRgbSpace(theme.neutral90)!;
    if (theme.neutral50) s['--pop-neutral-50'] = hexToRgbSpace(theme.neutral50)!;
    if (theme.neutral20) s['--pop-neutral-20'] = hexToRgbSpace(theme.neutral20)!;
    if (theme.neutral10) s['--pop-neutral-10'] = hexToRgbSpace(theme.neutral10)!;

    if (theme.accentRed90) s['--pop-accent-red-90'] = hexToRgbSpace(theme.accentRed90)!;
    if (theme.accentRed50) s['--pop-accent-red-50'] = hexToRgbSpace(theme.accentRed50)!;
    if (theme.accentRed10) s['--pop-accent-red-10'] = hexToRgbSpace(theme.accentRed10)!;

    // Optional: make the primary gradient auto-follow primary90/50 if either is overridden.
    if (s['--pop-primary-90'] || s['--pop-primary-50']) {
      const p90 = s['--pop-primary-90'] ?? 'var(--pop-primary-90)';
      const p50 = s['--pop-primary-50'] ?? 'var(--pop-primary-50)';
      s['--pop-grad-primary-9050'] = `linear-gradient(to right, rgb(${p90}), rgb(${p50}))`;
    }

    //console.debug(process.env.REACT_APP_POPSIZEPRIMARY90);
    console.debug(s);

    return s as React.CSSProperties;
  }, [theme]);

  return (
    <div className="pop-theme" style={style}>
      {children}
    </div>
  );
}
